import CategoryType from '../enum/Category';
import ICategory from '../models/ICategory';

import { toCents } from './cart-price';

export const ALL_YOU_CAN_EAT_BADGE_LABEL = 'Rodízio';

export const ALL_YOU_CAN_EAT_PRICE_LABEL = 'Incluso no rodízio';

/**
 * Whether a rodízio (all-you-can-eat) item is free at the value being shown, so that value reads as
 * included instead of R$ 0,00.
 *
 * `total` is in reais, like every price helper in this codebase, and is compared in cents. Neither
 * half is enough on its own: a rodízio item with a required paid complement still costs something
 * and keeps its price, and a zero alone proves nothing because `hidePriceOnShowCase` makes the API
 * zero every price.
 */
export const isIncludedInAllYouCanEat = (
  item: { isAllYouCanEat?: boolean } | null | undefined,
  total: number,
): boolean => !!item?.isAllYouCanEat && toCents(total) === 0;

/**
 * Whether a category is a rodízio (all-you-can-eat) section, so it can be labelled with the same
 * wording the items carry.
 *
 * Takes a structural `Pick` rather than `ICategory` on purpose: the products page keeps its own
 * `ILocalCategory` shape, which already carries `type`, and the synthetic tabs ("Todas",
 * "Promoções", "Mais vendidos") are fixed to `STANDARD` both here and on the server, so they never
 * match. The badge is a hint about the section: the authoritative per-item label stays
 * `isIncludedInAllYouCanEat`.
 */
export const isAllYouCanEatCategory = (category?: Pick<ICategory, 'type'> | null): boolean =>
  category?.type === CategoryType.ALL_YOU_CAN_EAT;

/**
 * The categories without the rodízio sections, for when the table is not cleared for rodízio.
 *
 * Returns the input itself when nothing is removed. Consumers memoize on these arrays, and a new
 * reference on every revalidation would re-run them for nothing: `categoriesGrouped` in the products
 * page collapses the sections again whenever the arrays change.
 */
export const withoutAllYouCanEatCategories = <T extends Pick<ICategory, 'type'>>(
  categories: T[],
): T[] => {
  const kept = categories.filter((category) => !isAllYouCanEatCategory(category));

  return kept.length === categories.length ? categories : kept;
};

/**
 * The products without the rodízio items. A product is removed only when its flag is exactly `true`,
 * so an older API build that answers without the flag keeps every product. Same reference rule as
 * `withoutAllYouCanEatCategories`.
 */
export const withoutAllYouCanEatProducts = <T extends { isAllYouCanEat?: boolean }>(
  products: T[],
): T[] => {
  const kept = products.filter((product) => product.isAllYouCanEat !== true);

  return kept.length === products.length ? products : kept;
};

/**
 * Identity of one rodízio check: which SimpleSolution installation, which card and which table
 * session. `null` when there is nothing to ask about (no installation or no valid card number).
 *
 * The endpoint only ever receives the card number. The session revision is here so an answer about
 * a previous session never clears the rodízio for a new one that happens to use the same number:
 * every table is a card, and card numbers are reused.
 */
export const buildAllYouCanEatGateKey = (
  baseURL: string | undefined,
  cardNumber: number,
  sessionRevision: number,
): string | null => {
  const url = baseURL?.trim();

  if (!url || !Number.isInteger(cardNumber) || cardNumber <= 0) return null;

  return `${url}|${cardNumber}|${sessionRevision}`;
};

/**
 * Whether the active category is a rodízio section the gate is hiding, so the page has to fall back
 * to "Todas" instead of showing an empty section.
 *
 * Checked against the raw list, not the visible one: while the catalog is still loading the list is
 * empty, and "not among the visible categories" would send the customer back for no reason.
 */
export const isActiveCategoryHiddenByGate = (
  activeCategoryId: number,
  rawCategories: Pick<ICategory, 'id' | 'type'>[],
  hideAllYouCanEat: boolean,
): boolean =>
  hideAllYouCanEat &&
  activeCategoryId > 0 &&
  rawCategories.some(
    (category) => category.id === activeCategoryId && isAllYouCanEatCategory(category),
  );
